import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Dialog from 'pages/Dashboard/components/Dialog';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import noop from 'lodash/noop';
import Button from 'pages/Dashboard/components/Button';
import IconButton from '@mui/material/IconButton';
import {
  ServiceFacilityDTO,
  AppointmentPaymentMethodDTO,
  EncounterWithCustomCodesViewModel,
  PatientDTO,
  PatientVisitDTO,
  SquareTerminalPaymentMethodDTO,
  UserDTO,
  VisitNoteBillingCustomServiceCodeDTO,
} from 'dtos';
import {
  PatientPaymentTransactionType,
} from 'dtos/patientPaymentTransactionType';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import PatientEncounterSelectForPurchase from 'views/EMR/components/PatientEncounterSelectForPurchase';
import ServicesLookup from 'views/EMR/components/ServiceLookup';
import {
  filterCustomCodes,
  normalizeCustomServiceCodeOption,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import sumBy from 'lodash/sumBy';
import PaymentFields from 'views/EMR/Billing/PaymentFields';
import {
  appointmentsEventChannel,
  patientEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useProcessPayment,
} from 'views/EMR/hooks/billing';
import {
  formatMoney,
} from '@populateui/utils';
import Row from 'pages/Dashboard/components/Row';
import ToolTip from 'core/components/Tooltip';
import DoctorSelectPopover from 'views/EMR/components/DoctorSelectPopover';
import Label from 'pages/Dashboard/components/Label';
import {
  useGetFavoriteCodes,
} from 'views/EMR/Settings/hooks';
import useSignalREffect from 'views/EMR/hooks/signalR';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useMixpanelTrack,
} from 'core/hooks';
import {
  validatePatient,
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  useFormAutoFocus,
} from 'views/EMR/hooks/common';
import defer from 'lodash/defer';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import ReceiptEmailDialog from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/ReceiptEmailDialog';

// A purchase always records a payment transaction; the purchase response doesn't return its type.
const PURCHASE_TRANSACTION_TYPE = PatientPaymentTransactionType.Payment;

type CustomServiceCode = VisitNoteBillingCustomServiceCodeDTO & {
  defaultBilledAmountPerUnit?: number | null;
};

export type PurchaseDialogParams = {
  patient?: PatientDTO;
  encounter?: EncounterWithCustomCodesViewModel;
  customServiceCodes?: CustomServiceCode[];
};

type Props = PurchaseDialogParams & {
  onClose: () => void;
};

export type PurchaseForm = {
  patient?: PatientDTO;
  encounter?: EncounterWithCustomCodesViewModel | null;
  customServiceCodes?: CustomServiceCode[];
  currentEncounterPayment?: number;
  paymentMethod?: AppointmentPaymentMethodDTO;
  doctor?: UserDTO | null;
  serviceFacility?: ServiceFacilityDTO | null;
};

type CreatePurchaseFormProps = {
  onClose: () => void;
  dialogRef: RefObject<HTMLDivElement>;
  setPatientPaymentId: Dispatch<SetStateAction<number | null>>;
};

function CreatePurchaseForm({
  onClose,
  dialogRef,
  setPatientPaymentId,
}: CreatePurchaseFormProps) {
  const mixPanelTrack = useMixpanelTrack();

  const [doctorPopoverAnchorElement, setShowDoctorPopover] = useState<HTMLElement | null>(null);
  const openedDoctorPopover = !isNil(doctorPopoverAnchorElement);

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    formState: { isValid, isSubmitting },
    watch,
    setValue,
  } = useFormContext<PurchaseForm>();

  const [
    patient,
    encounter,
    customServiceCodes = [],
    doctor,
    serviceFacility,
    paymentMethod,
  ] = watch(['patient', 'encounter', 'customServiceCodes', 'doctor', 'serviceFacility', 'paymentMethod']);

  const encounterSelectWrapperRef = useRef<HTMLDivElement>(null);
  const patientLookupInputRef = useFormAutoFocus({ enabled: isNil(patient?.patientId) });

  const isTerminal = !isNil((paymentMethod as SquareTerminalPaymentMethodDTO)?.deviceId);

  const onPaymentComplete = useCallback((patientPaymentId?: number) => {
    setPatientPaymentId(patientPaymentId ?? 0);

    appointmentsEventChannel.emit('onEventUpdated', encounter?.encounterId);
    paymentsEventChannel.emit('onPaymentsDataChanged', 'patient-payments');
    paymentsEventChannel.emit('onPatientBalanceChange');
  }, [paymentMethod, encounter?.encounterId, isTerminal]);

  const {
    createPayment,
    cancelling,
    isCancelable,
    cancelCurrentPayment,
  } = useProcessPayment({
    onPaymentComplete,
    paymentMethod,
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    const codes = uniqBy([
      ...customServiceCodes,
      ...(encounter?.customServiceCodes ?? []),
    ], 'serviceName');
    setValue('customServiceCodes', codes);
  }, [encounter]);

  const total = useMemo(() => sumBy(
    customServiceCodes,
    (code) => ((code?.defaultBilledAmountPerUnit ?? 0) * (code?.numberOfUnits ?? 0)),
  ), [customServiceCodes]);

  useEffect(() => {
    setValue('currentEncounterPayment', total);
  }, [total]);

  const onSubmit = async (data: PurchaseForm) => {
    await createPayment(data, true);
    mixPanelTrack('SubmitProductPurchase');
  };

  const {
    data: favoriteCodes,
    refetch: refetchFavoriteCode,
  } = useGetFavoriteCodes();

  return (
    <>
      <div className="flex flex-col flex-1 gap-form-row populate-form overflow-y-auto px-5 py-1">
        <Controller
          name="patient"
          control={control}
          rules={{
            required: 'Select patient',
            validate: validatePatient,
          }}
          render={({ field, fieldState: { invalid } }) => (
            <PatientSelect
              {...field}
              value={field?.value as PatientCompact}
              className="!mb-0"
              label="Patient"
              error={invalid}
              id="patient-lookup"
              required
              readonly={isSubmitting}
              placeholder="Enter patient name, phone or date of birth"
              allowDefault
              inputRef={patientLookupInputRef as RefObject<HTMLInputElement>}
              containerRef={dialogRef}
              onAddPatient={(phoneNumber: string) => {
                patientEventChannel.emit('onCreatePatientDialog', {
                  defaultPhoneNumber: /^\d{10,}$/.test(phoneNumber) ? phoneNumber : '',
                  onPatientSelect: field.onChange,
                  dialogHeight: dialogRef?.current?.offsetHeight,
                });
              }}
              disabled={isSubmitting}
            />
          )}
        />
        {(isNil(doctor) || isNil(serviceFacility))
          ? (
            <Controller
              name="encounter"
              control={control}
              rules={{ required: 'Select encounter' }}
              render={({ field, fieldState: { invalid } }) => (
                <PatientEncounterSelectForPurchase
                  {...field}
                  autoFocus={!isNil(patient?.patientId)}
                  error={invalid}
                  className="!mb-0"
                  classes={{
                    select: '!text-left',
                    root: 'w-1/2',
                  }}
                  wrapperRef={encounterSelectWrapperRef as RefObject<HTMLDivElement>}
                  label="Encounter"
                  id="encounter-select"
                  patient={patient}
                  encounterId={encounter?.encounterId}
                  onChange={(encounter) => {
                    const value = encounter as PatientVisitDTO;
                    if (value?.encounterId === 0) {
                      setShowDoctorPopover(encounterSelectWrapperRef.current as HTMLDivElement);
                    } else {
                      setValue('doctor', null);
                      setValue('serviceFacility', null);
                      field.onChange(value);
                    }
                  }}
                  placeholder="Select Encounter"
                  required={isNil(doctor) || isNil(serviceFacility)}
                  autoSelect
                />
              )}
            />
          )
          : (
            <div className="w-1/2 flex flex-col gap-1">
              <Label
                required
                id="encounter"
                label="Encounter"
              />
              <div
                role="button"
                tabIndex={-1}
                onKeyDown={noop}
                ref={encounterSelectWrapperRef as RefObject<HTMLDivElement>}
                onClick={() => {
                  setShowDoctorPopover(encounterSelectWrapperRef?.current as HTMLDivElement);
                }}
                className="transition-colors flex w-full p-2 border rounded border-gray-300 text-xs justify-between cursor-pointer hover:border-blue-500"
              >
                <div className="flex gap-1 flex-1 flex-col">
                  <div className="font-semibold">
                    {getDisplayName(doctor.formattedNames)}
                  </div>
                  <div>{serviceFacility?.nickname}</div>
                </div>
                <ToolTip title="Remove">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setValue('doctor', null);
                      setValue('serviceFacility', null);
                      trigger();
                    }}
                    className="!h-6"
                    color="error"
                  >
                    <CloseIcon className="!w-5 !h-5 !text-gray-600" />
                  </IconButton>
                </ToolTip>
              </div>
            </div>
          )}
        {openedDoctorPopover && (
          <DoctorSelectPopover
            anchorElement={doctorPopoverAnchorElement}
            value={{ doctor, serviceFacility }}
            onClose={() => { setShowDoctorPopover(null); }}
            onConfirm={(value) => {
              setValue('doctor', value?.doctor);
              setValue('serviceFacility', value?.serviceFacility);
              setValue('encounter', null);
              defer(() => {
                encounterSelectWrapperRef?.current?.focus();
              });
            }}
          />
        )}
        <div className="flex w-full flex-col">
          <Controller
            name="customServiceCodes"
            control={control}
            rules={{ required: 'Add custom service codes' }}
            render={({ field, fieldState: { invalid } }) => (
              <ServicesLookup
                {...field}
                value={field?.value as CustomServiceCode[]}
                type="CustomCode"
                label="Custom Services"
                dropdownId="custom-code-selection"
                id="customServiceCodes"
                placeholder="Search Custom Services"
                required
                error={invalid}
                visitNoteId={0}
                patientId={patient?.patientId}
                externalFilter={filterCustomCodes}
                showCustomServiceCodePrice
                className="!gap-2"
                classes={{
                  root: '!w-full pb-2',
                  table: 'w-full',
                  label: 'text-xs text-gray-500',
                  list: 'max-h-[200px]',
                }}
                favorites={favoriteCodes?.favoriteCustomServiceCodes ?? []}
                refetchFavoriteCode={refetchFavoriteCode}
                actions={['pin']}
                normalizeOption={normalizeCustomServiceCodeOption}
              />
            )}
          />
          {customServiceCodes.length > 0 && (
            <Row
              title="Total"
              className="!w-48 !border-none !justify-start gap-4 !p-0 font-semibold"
            >
              {formatMoney(total)}
            </Row>
          )}
        </div>
        <PaymentFields allowOnline={false} />
      </div>
      <div className="flex flex-row-reverse justify-between items-center p-5">
        {isCancelable ? (
          <Button
            variant="contained"
            color="primary"
            onClick={cancelCurrentPayment}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Cancel'}
          </Button>
        ) : (
          <DisabledFormTooltip>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              Confirm
            </Button>
          </DisabledFormTooltip>
        )}
        <Button
          variant="outlined"
          type="button"
          onClick={handleClose}
        >
          Close
        </Button>
      </div>
    </>
  );
}

type PurchaseReceiptProps = {
  patientPaymentId: number;
  patient?: PatientDTO;
  ready: boolean;
  onClose: () => void;
};

export function PurchaseReceipt({
  patientPaymentId: paymentId,
  patient,
  ready,
  onClose,
}: PurchaseReceiptProps) {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  return (
    <>
      <div className="flex flex-col gap-4 px-5 text-xs h-48">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="!w-5 !h-5 !text-populate-green-1" />
          <span className="font-semibold">Purchase successful</span>
        </div>
        <div className="flex items-center gap-2">
          {ready ? (
            <>
              <CheckCircleIcon className="!w-5 !h-5 !text-populate-green-1" />
              <span className="font-semibold">Receipt is ready</span>
            </>
          ) : (
            <>
              <Spinner className="!w-5 !h-5" />
              <span>Receipt is being prepared...</span>
            </>
          )}
        </div>
      </div>
      <div className="flex justify-between items-center p-5">
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
        <ToolTip
          title="Preparing receipt..."
          hide={ready}
        >
          <span className={clsx('flex gap-4', { 'cursor-not-allowed': !ready })}>
            <Button
              variant="outlined"
              color="primary"
              disabled={!ready}
              onClick={() => { setEmailDialogOpen(true); }}
            >
              Email
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!ready}
              onClick={() => {
                paymentsEventChannel.emit(
                  'onPaymentPrint',
                  { patientPaymentTransactionId: paymentId },
                  onClose,
                );
              }}
            >
              Print
            </Button>
          </span>
        </ToolTip>
      </div>
      {emailDialogOpen && (
        <ReceiptEmailDialog
          payment={{
            patientPaymentTransactionId: paymentId,
            type: PURCHASE_TRANSACTION_TYPE,
            patient,
          }}
          onClose={() => { setEmailDialogOpen(false); }}
        />
      )}
    </>
  );
}

export default function CreatePurchaseDialog({
  patient,
  encounter,
  customServiceCodes,
  onClose,
}: Props) {
  const form = useForm<PurchaseForm>({
    mode: 'all',
    defaultValues: {
      patient: patient ?? { dateOfBirth: '' },
      encounter,
      customServiceCodes,
    },
  });

  const selectedPatient = useWatch({ control: form.control, name: 'patient' });

  const [patientPaymentId, setPatientPaymentId] = useState<number | null>(null);
  const [readyInvoiceIds, setReadyInvoiceIds] = useState<number[]>([]);

  const userInfo = useUserInfo();
  const currentUserId = userInfo?.user?.userId;

  // Subscribed here rather than in PurchaseReceipt: the invoice event can arrive before the
  // receipt view mounts, and a listener registered after that would miss it.
  useSignalREffect('OnInvoiceUpdated', ({ userId, patientPaymentId: invoicePaymentId }) => {
    if (userId === currentUserId && !isNil(invoicePaymentId)) {
      setReadyInvoiceIds((ids) => (ids.includes(invoicePaymentId) ? ids : [...ids, invoicePaymentId]));
    }
  }, [currentUserId]);

  const dialogRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;

  return (
    <Dialog
      open
      title="Purchase Product"
      onClose={onClose}
      onConfirm={noop}
      confirmText="Create"
      className="!w-160 overflow-y-hidden populate-form !space-y-0 !p-0"
      titleClassName="p-5"
      maxWidth="lg"
      dialogRef={dialogRef}
      autoFocusOptions={{ enabled: false }}
      disableEscapeKeyDown
      disableBackdropClick
    >
      {isNil(patientPaymentId) ? (
        <FormProvider {...form}>
          <CreatePurchaseForm
            onClose={onClose}
            dialogRef={dialogRef}
            setPatientPaymentId={setPatientPaymentId}
          />
        </FormProvider>
      ) : (
        <PurchaseReceipt
          patientPaymentId={patientPaymentId}
          patient={selectedPatient}
          ready={readyInvoiceIds.includes(patientPaymentId)}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
}
